import { useState } from "react";
import moment from "moment";

// Renderers for the typed `blocks` array of an AiResponse (see
// makani-ai-service/docs/ASK_MAKANI_AI_API.md). Unknown block types, colors and
// chart types fall back to sensible defaults instead of crashing — the service
// may add new kinds without a frontend release.

export const BLOCK_COLORS = {
  green: "#16A34A",
  amber: "#F97316",
  red: "#EF4444",
  blue: "#0065FD",
  gray: "#989BA0",
};

const CHART_PALETTE = ["#0065FD", "#16A34A", "#F97316", "#EF4444", "#8B5CF6", "#0EA5E9", "#7C829A"];

const STATUS_PILL_STYLES = {
  green: "bg-[#DCFCE7] text-[#15803D]",
  amber: "bg-[#FFEDD5] text-[#C2410C]",
  red: "bg-[#FEE2E2] text-[#B91C1C]",
  blue: "bg-[#DBEAFE] text-[#1D4ED8]",
  gray: "bg-[#F1F2F7] text-[#5F6477]",
};

const statusColor = (value) => {
  const v = String(value ?? "").toLowerCase();
  if (["approved", "completed", "complete", "paid", "active", "done", "closed", "resolved"].some((s) => v.includes(s)))
    return "green";
  if (["pending", "in progress", "open", "waiting", "draft", "scheduled"].some((s) => v.includes(s))) return "amber";
  if (["rejected", "overdue", "cancelled", "canceled", "failed", "denied", "expired"].some((s) => v.includes(s)))
    return "red";
  return "gray";
};

const pointColor = (point, index) => BLOCK_COLORS[point?.color] || CHART_PALETTE[index % CHART_PALETTE.length];

const toNumber = (value) => {
  const num = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(num) ? num : 0;
};

const formatNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString("en-US") : String(value ?? "");
};

const formatCell = (value, type) => {
  if (value === null || value === undefined || value === "") return "-";
  switch (type) {
    case "date": {
      // Date-only values ("2024-05-12") must be parsed as local dates, not
      // through new Date() which shifts them across timezones.
      const parsed = moment(value, ["YYYY-MM-DD", moment.ISO_8601], true);
      return parsed.isValid() ? parsed.format("MMM D, YYYY") : String(value);
    }
    case "currency": {
      const num = Number(String(value).replace(/[^0-9.-]/g, ""));
      return Number.isFinite(num)
        ? num.toLocaleString("en-US", { style: "currency", currency: "USD" })
        : String(value);
    }
    case "number":
      return formatNumber(value);
    default:
      return String(value);
  }
};

// --- Markdown-lite ---------------------------------------------------------
// The `message` / text blocks are markdown, but in practice only need bold and
// line breaks; render those without pulling in a markdown dependency.

const renderInlineMarkdown = (line) =>
  line
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, index) =>
      part.startsWith("**") && part.endsWith("**") ? (
        <strong key={index} className="font-OpenSansBold">
          {part.slice(2, -2)}
        </strong>
      ) : (
        part
      ),
    );

export const MarkdownLite = ({ text, className = "" }) => {
  if (!text) return null;
  return (
    <div className={className}>
      {String(text)
        .split("\n")
        .filter((line, index, lines) => line.trim() !== "" || (index > 0 && lines[index - 1].trim() !== ""))
        .map((line, index) => (
          <p key={index} className={index > 0 ? "mt-2" : ""}>
            {renderInlineMarkdown(line)}
          </p>
        ))}
    </div>
  );
};

// --- Metric ----------------------------------------------------------------

const PoDocumentIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0065FD" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v5h5" />
    <path d="M12 10.2v7.6" />
    <path d="M14.2 11.6h-3.3a1.45 1.45 0 1 0 0 2.9h2.2a1.45 1.45 0 1 1 0 2.9H9.8" />
  </svg>
);

const ClockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0065FD" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" />
  </svg>
);

const ChartMetricIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0065FD" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 20V10" />
    <path d="M10 20V4" />
    <path d="M16 20v-7" />
    <path d="M22 20H2" />
  </svg>
);

const METRIC_ICONS = {
  po: PoDocumentIcon,
  hours: ClockIcon,
  time: ClockIcon,
  chart: ChartMetricIcon,
};

const MetricBlock = ({ block }) => {
  const Icon = METRIC_ICONS[block.icon] || PoDocumentIcon;
  return (
    <div>
      {block.breakdown?.length > 0 && (
        <div className="mb-4 font-OpenSansRegular text-[14px] leading-5 text-[#989BA0]">
          Breakdown:{" "}
          {block.breakdown.map((item, index) => (
            <span key={index}>
              <span
                className="font-OpenSansSemiBold"
                style={{ color: BLOCK_COLORS[item.color] || BLOCK_COLORS.gray }}
              >
                {item.label}: {item.value}
              </span>
              {index < block.breakdown.length - 1 && ", "}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-6 rounded-[12px] border border-[#DBEAFE] bg-[#EFF6FF] p-6">
        <div className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          <Icon />
        </div>
        <div className="min-w-0">
          <div className="font-OpenSansBold text-[12px] uppercase leading-4 tracking-[0.6px] text-[#2563EB]">
            {block.label}
          </div>
          <div className="flex flex-wrap items-end gap-x-2">
            <span className="font-OpenSansBold text-[30px] leading-9 text-[#333333]">{block.value}</span>
            {block.unit && (
              <span className="font-OpenSansRegular text-[18px] leading-7 text-[#989BA0]">{block.unit}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Table -----------------------------------------------------------------

const COLLAPSED_ROW_COUNT = 5;

const renderCell = (value, type) => {
  if (type === "status") {
    const color = statusColor(value);
    return (
      <span
        className={`inline-flex rounded-full px-2.5 py-1 font-OpenSansBold text-[12px] leading-4 ${
          STATUS_PILL_STYLES[color] || STATUS_PILL_STYLES.gray
        }`}
      >
        {String(value ?? "-")}
      </span>
    );
  }
  if (type === "link") {
    return <span className="font-OpenSansSemiBold text-[14px] leading-5 text-[#0065FD]">{String(value ?? "-")}</span>;
  }
  const textColor = type === "text" ? "text-[#989BA0]" : "text-[#333333]";
  return <span className={`font-OpenSansRegular text-[14px] leading-5 ${textColor}`}>{formatCell(value, type)}</span>;
};

const TableBlock = ({ block }) => {
  const [expanded, setExpanded] = useState(false);
  const allRows = block.rows || [];
  const columns = block.columns || [];
  const visibleRows = expanded ? allRows : allRows.slice(0, COLLAPSED_ROW_COUNT);
  const totalRecords = block.totalRecords ?? allRows.length;
  const canExpand = !expanded && allRows.length > COLLAPSED_ROW_COUNT;
  const partialData = block.hasMore && allRows.length < totalRecords;

  return (
    <div className="overflow-hidden rounded-[12px] border border-[#EDF2F7] bg-white">
      {block.title && (
        <div className="border-b border-[#EDF2F7] px-6 py-4 font-OpenSansSemiBold text-[15px] leading-5 text-[#333333]">
          {block.title}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#EDF2F7] bg-[#F8FAFC]">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-4 text-left font-OpenSansSemiBold text-[14px] leading-5 text-[#333333]"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b border-[#EDF2F7] last:border-b-0">
                {columns.map((column) => (
                  <td key={column.key} className="px-6 py-4">
                    {renderCell(row[column.key], column.type)}
                  </td>
                ))}
              </tr>
            ))}
            {visibleRows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length || 1}
                  className="px-6 py-6 text-center font-OpenSansRegular text-[14px] text-[#989BA0]"
                >
                  No records
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {canExpand ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex w-full items-center justify-center gap-1 border-t border-[#EDF2F7] bg-[#F8FAFC] px-6 py-3 font-OpenSansBold text-[12px] leading-4 text-[#0065FD] hover:bg-[#F1F5FB]"
        >
          View all {formatNumber(totalRecords)} records
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2.5 8h11M9.5 4l4 4-4 4" />
          </svg>
        </button>
      ) : (
        partialData && (
          <div className="border-t border-[#EDF2F7] bg-[#F8FAFC] px-6 py-3 text-center font-OpenSansRegular text-[12px] leading-4 text-[#989BA0]">
            Showing {formatNumber(allRows.length)} of {formatNumber(totalRecords)} records — export CSV for the full
            list
          </div>
        )
      )}
    </div>
  );
};

// --- Charts ----------------------------------------------------------------

const BarChart = ({ points }) => {
  const max = Math.max(...points.map((p) => toNumber(p.value)), 1);
  return (
    <div className="flex flex-col gap-3">
      {points.map((point, index) => (
        <div key={index} className="flex items-center gap-3">
          <div className="w-[140px] shrink-0 truncate text-right font-OpenSansRegular text-[13px] leading-5 text-[#7C829A]">
            {point.label}
          </div>
          <div className="h-[22px] flex-1 overflow-hidden rounded-[6px] bg-[#EFF1F7]">
            <div
              className="h-full rounded-[6px] transition-all duration-500"
              style={{ width: `${(toNumber(point.value) / max) * 100}%`, backgroundColor: pointColor(point, index) }}
            />
          </div>
          <div className="w-[64px] shrink-0 font-OpenSansSemiBold text-[13px] leading-5 text-[#333333]">
            {formatNumber(toNumber(point.value))}
          </div>
        </div>
      ))}
    </div>
  );
};

const DonutChart = ({ block }) => {
  const points = block.points || [];
  const total = points.reduce((sum, point) => sum + toNumber(point.value), 0) || 1;
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="flex flex-wrap items-center gap-8">
      <div className="relative h-[160px] w-[160px] shrink-0">
        <svg viewBox="0 0 160 160" className="h-full w-full -rotate-90">
          <circle cx="80" cy="80" r={radius} fill="none" stroke="#EFF1F7" strokeWidth="20" />
          {points.map((point, index) => {
            const dash = (toNumber(point.value) / total) * circumference;
            const segment = (
              <circle
                key={index}
                cx="80"
                cy="80"
                r={radius}
                fill="none"
                stroke={pointColor(point, index)}
                strokeWidth="20"
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
              />
            );
            offset += dash;
            return segment;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-OpenSansBold text-[20px] leading-6 text-[#333333]">
            {block.totalValue || formatNumber(total)}
          </span>
          {block.totalLabel && (
            <span className="mt-1 px-3 text-center font-OpenSansRegular text-[11px] leading-4 text-[#989BA0]">
              {block.totalLabel}
            </span>
          )}
        </div>
      </div>
      <div className="flex min-w-[180px] flex-1 flex-col gap-2">
        {points.map((point, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="h-[10px] w-[10px] shrink-0 rounded-full" style={{ backgroundColor: pointColor(point, index) }} />
            <span className="flex-1 truncate font-OpenSansRegular text-[13px] leading-5 text-[#7C829A]">
              {point.label}
            </span>
            <span className="font-OpenSansSemiBold text-[13px] leading-5 text-[#333333]">
              {formatNumber(toNumber(point.value))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LineChart = ({ points }) => {
  const width = 560;
  const height = 190;
  const padding = { top: 16, right: 20, bottom: 32, left: 20 };
  const values = points.map((p) => toNumber(p.value));
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const x = (index) =>
    points.length > 1
      ? padding.left + (index * (width - padding.left - padding.right)) / (points.length - 1)
      : width / 2;
  const y = (value) => height - padding.bottom - ((value - min) / range) * (height - padding.top - padding.bottom);
  const path = values.map((value, index) => `${index === 0 ? "M" : "L"} ${x(index)} ${y(value)}`).join(" ");

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full min-w-[420px]">
        {[0.25, 0.5, 0.75, 1].map((fraction) => {
          const gy = height - padding.bottom - fraction * (height - padding.top - padding.bottom);
          return <line key={fraction} x1={padding.left} y1={gy} x2={width - padding.right} y2={gy} stroke="#EFF1F7" />;
        })}
        <line
          x1={padding.left}
          y1={height - padding.bottom}
          x2={width - padding.right}
          y2={height - padding.bottom}
          stroke="#EDF2F7"
        />
        <path d={path} fill="none" stroke="#0065FD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {values.map((value, index) => (
          <circle key={index} cx={x(index)} cy={y(value)} r="3.5" fill="#fff" stroke="#0065FD" strokeWidth="2" />
        ))}
        {points.map((point, index) => (
          <text
            key={index}
            x={x(index)}
            y={height - 10}
            textAnchor="middle"
            fontSize="11"
            fill="#989BA0"
            fontFamily="OpenSansRegular"
          >
            {point.label}
          </text>
        ))}
      </svg>
    </div>
  );
};

const ChartBlock = ({ block }) => {
  const points = block.points || [];
  return (
    <div className="rounded-[12px] border border-[#EDF2F7] bg-white p-6">
      {(block.title || block.totalValue) && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          {block.title && (
            <span className="font-OpenSansSemiBold text-[15px] leading-5 text-[#333333]">{block.title}</span>
          )}
          {block.totalValue && block.chartType !== "donut" && (
            <span className="font-OpenSansRegular text-[13px] leading-5 text-[#989BA0]">
              {block.totalLabel && `${block.totalLabel}: `}
              <span className="font-OpenSansSemiBold text-[#333333]">{block.totalValue}</span>
            </span>
          )}
        </div>
      )}
      {block.chartType === "donut" ? (
        <DonutChart block={block} />
      ) : block.chartType === "line" ? (
        <LineChart points={points} />
      ) : (
        <BarChart points={points} />
      )}
    </div>
  );
};

// --- Cards -----------------------------------------------------------------

const CARD_HIGHLIGHT_STYLES = {
  green: "bg-[#F0FDF4] border-[#BBF7D0]",
  blue: "bg-[#EFF6FF] border-[#DBEAFE]",
  amber: "bg-[#FFF7ED] border-[#FED7AA]",
  red: "bg-[#FEF2F2] border-[#FECACA]",
  gray: "bg-[#F8FAFC] border-[#EDF2F7]",
};

const CardsBlock = ({ block }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {(block.cards || []).map((card, index) => (
      <div
        key={index}
        className={`rounded-[12px] border p-5 ${
          card.highlight ? CARD_HIGHLIGHT_STYLES[card.color] || CARD_HIGHLIGHT_STYLES.blue : "border-[#EDF2F7] bg-white"
        }`}
      >
        <div
          className="font-OpenSansBold text-[11px] uppercase leading-4 tracking-[0.6px]"
          style={{ color: BLOCK_COLORS[card.color] || "#7C829A" }}
        >
          {card.label}
        </div>
        <div className="mt-2 flex flex-wrap items-end gap-x-2">
          <span className="font-OpenSansBold text-[24px] leading-8 text-[#333333]">{card.value}</span>
          {card.unit && <span className="font-OpenSansRegular text-[14px] leading-6 text-[#989BA0]">{card.unit}</span>}
        </div>
        {card.sublabel && (
          <div className="mt-1 font-OpenSansRegular text-[13px] leading-5 text-[#989BA0]">{card.sublabel}</div>
        )}
      </div>
    ))}
  </div>
);

// --- Dispatcher ------------------------------------------------------------

export const AiBlock = ({ block }) => {
  switch (block?.type) {
    case "text":
      return (
        <MarkdownLite text={block.markdown} className="font-OpenSansRegular text-[14px] leading-6 text-[#333333]" />
      );
    case "metric":
      return <MetricBlock block={block} />;
    case "table":
      return <TableBlock block={block} />;
    case "chart":
      return <ChartBlock block={block} />;
    case "cards":
      return <CardsBlock block={block} />;
    default:
      // Unknown block type — degrade to any markdown-ish text it carries.
      return block?.markdown || block?.message ? (
        <MarkdownLite
          text={block.markdown || block.message}
          className="font-OpenSansRegular text-[14px] leading-6 text-[#333333]"
        />
      ) : null;
  }
};

export const AiBlockList = ({ blocks }) =>
  (blocks || []).map((block, index) => <AiBlock key={index} block={block} />);
